const DB_NAME = "privatelocalchat-db";
const DB_VERSION = 3;

export type StoreName =
  | "threads"
  | "messages"
  | "settings"
  | "profiles"
  | "installs"
  | "diagnostics"
  | "telemetryOutbox"
  | "installCheckpoints";

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function openAppDatabase(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains("threads")) {
          const store = db.createObjectStore("threads", { keyPath: "id" });
          store.createIndex("updatedAt", "updatedAt");
        }

        if (!db.objectStoreNames.contains("messages")) {
          const store = db.createObjectStore("messages", { keyPath: "id" });
          store.createIndex("threadId", "threadId");
          store.createIndex("createdAt", "createdAt");
        }

        if (!db.objectStoreNames.contains("settings")) {
          db.createObjectStore("settings", { keyPath: "id" });
        }

        if (!db.objectStoreNames.contains("profiles")) {
          db.createObjectStore("profiles", { keyPath: "id" });
        }

        if (!db.objectStoreNames.contains("installs")) {
          db.createObjectStore("installs", { keyPath: "id" });
        }

        if (!db.objectStoreNames.contains("diagnostics")) {
          db.createObjectStore("diagnostics", { keyPath: "id" });
        }

        if (!db.objectStoreNames.contains("telemetryOutbox")) {
          const store = db.createObjectStore("telemetryOutbox", { keyPath: "id" });
          store.createIndex("queuedAt", "queuedAt");
        }

        if (!db.objectStoreNames.contains("installCheckpoints")) {
          const store = db.createObjectStore("installCheckpoints", { keyPath: "key" });
          store.createIndex("scope", "scope");
          store.createIndex("file", "file");
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  return dbPromise;
}

export async function runTransaction<T>(
  storeName: StoreName,
  mode: IDBTransactionMode,
  handler: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openAppDatabase();
  const transaction = db.transaction(storeName, mode);
  const store = transaction.objectStore(storeName);
  const result = await handler(store);

  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });

  return result;
}

export async function getRecord<T>(
  storeName: StoreName,
  key: IDBValidKey,
): Promise<T | undefined> {
  return runTransaction(storeName, "readonly", async (store) => {
    return requestToPromise(store.get(key));
  });
}

export async function putRecord<T>(
  storeName: StoreName,
  value: T,
): Promise<void> {
  await runTransaction(storeName, "readwrite", async (store) => {
    await requestToPromise(store.put(value));
  });
}

export async function getAllRecords<T>(storeName: StoreName): Promise<T[]> {
  return runTransaction(storeName, "readonly", async (store) => {
    return requestToPromise(store.getAll());
  });
}

export async function countRecords(storeName: StoreName): Promise<number> {
  return runTransaction(storeName, "readonly", async (store) => {
    return requestToPromise(store.count());
  });
}
